import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/context/AuthContext";

const inputCls =
  "bg-transparent border-b border-[#222222] focus:border-[#FF3B30] outline-none text-white px-0 py-3 text-sm transition-colors w-full font-mono-plex placeholder-[#555555]";

export const EmployeeManager = () => {
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/staff/employees");
      setEmployees(data);
    } catch (e) {
      toast.error("Could not load team accounts.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/staff/employees", form);
      toast.success(`Account created for ${form.name}`);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (emp) => {
    if (!window.confirm(`Remove ${emp.name}'s account?`)) return;
    try {
      await api.delete(`/staff/employees/${emp.id}`);
      toast.success("Account removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="employee-manager" className="grid grid-cols-1 lg:grid-cols-2 gap-12">
      <form onSubmit={create} className="border border-[#222222] p-6 md:p-8">
        <h3 className="font-display font-bold uppercase text-white text-xl mb-6">Add employee</h3>
        <div className="space-y-5">
          <input
            data-testid="employee-name-input"
            required
            placeholder="FULL NAME"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className={inputCls}
          />
          <input
            data-testid="employee-email-input"
            required
            type="email"
            placeholder="EMAIL"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            className={inputCls}
          />
          <input
            data-testid="employee-password-input"
            required
            type="password"
            autoComplete="new-password"
            minLength={6}
            placeholder="PASSWORD (MIN 6 CHARS)"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            className={inputCls}
          />
          <button
            data-testid="employee-create-btn"
            type="submit"
            disabled={saving}
            className="w-full bg-[#FF3B30] text-white font-mono-plex uppercase tracking-[0.2em] text-xs font-bold py-3 hover:bg-white hover:text-black transition-colors disabled:opacity-50"
          >
            {saving ? "Creating..." : "Create account"}
          </button>
        </div>
      </form>

      <div>
        <h3 className="font-display font-bold uppercase text-white text-xl mb-6">Team accounts</h3>
        <div className="space-y-px bg-[#222222] border border-[#222222]">
          {employees.map((emp) => (
            <div
              key={emp.id}
              data-testid={`employee-row-${emp.id}`}
              className="bg-[#0A0A0A] p-5 flex items-center justify-between gap-4"
            >
              <div>
                <p className="text-white font-mono-plex text-sm font-bold">
                  {emp.name}{" "}
                  {emp.role === "admin" && <span className="text-[#FF3B30] text-xs ml-2">[ADMIN]</span>}
                </p>
                <p className="text-[#9A9A9A] font-mono-plex text-xs mt-1">{emp.email}</p>
              </div>
              {emp.role !== "admin" && (
                <button
                  data-testid={`employee-delete-${emp.id}`}
                  onClick={() => remove(emp)}
                  className="text-[#9A9A9A] hover:text-[#FF3B30] transition-colors p-2"
                  title="Remove"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
